/**
 * ComingSoonWaitlist — V143.
 *
 * Mini inline form on per-agent landing pages and the master funnel
 * for the 12 "Pripravujeme & testujeme" agents. Captures email +
 * optional note → POST /api/agent-waitlist/signup. After signup the
 * form swaps to a success state with the demand counter.
 *
 * Variants:
 *   - "inline" (compact, single-row) → master funnel card overlay
 *   - "full"  (vertical, includes note textarea) → landing page CTA block
 */
import { useState } from "react";
import axios from "axios";
import { Mail, Check, Loader2 } from "lucide-react";
import { API } from "../../config";
import { useLanguage } from "../../contexts/LanguageContext";
import { funnelT } from "../../translations-funnel";
import PreorderButton from "./PreorderButton";

export default function ComingSoonWaitlist({ agentSlug, agentName, eta, variant = "full", accent = "#FFB800" }) {
  const { language } = useLanguage();
  const ft = funnelT(language);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setErr(ft.wl_email_invalid);
      return;
    }
    setState("sending");
    setErr("");
    try {
      await axios.post(`${API}/agent-waitlist/signup`, {
        agent_slug: agentSlug,
        email: email.trim().toLowerCase(),
        note: note?.trim() || null,
      });
      setState("done");
    } catch (e) {
      setState("error");
      setErr(e?.response?.data?.detail || ft.wl_error_generic);
    }
  };

  if (state === "done") {
    return (
      <div
        data-testid={`waitlist-success-${agentSlug}`}
        className="border border-emerald-600/40 bg-emerald-500/10 p-4 sm:p-5"
      >
        <p className="text-[10px] uppercase tracking-[0.2em] font-black text-emerald-400 mb-2">
          <Check className="w-3 h-3 inline mr-1" /> {ft.wl_success_eyebrow}
        </p>
        <p className="text-sm text-emerald-100 leading-relaxed">
          {ft.wl_success_p_a} <strong>{agentName}</strong> {ft.wl_success_p_b} <strong>{ft.wl_success_offer}</strong>
        </p>
        <PreorderButton agentSlug={agentSlug} agentName={agentName} email={email} />
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <form
        onSubmit={submit}
        data-testid={`waitlist-form-${agentSlug}`}
        className="flex flex-col sm:flex-row gap-2"
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={ft.wl_inline_placeholder}
          data-testid={`waitlist-email-${agentSlug}`}
          className="flex-1 bg-black border border-gray-700 px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-amber-500 focus:outline-none"
        />
        <button
          type="submit"
          disabled={state === "sending"}
          data-testid={`waitlist-submit-${agentSlug}`}
          className="bg-amber-500 text-black font-black px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-amber-400 disabled:opacity-50 flex items-center justify-center gap-1.5"
        >
          {state === "sending" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
          {ft.wl_inline_cta}
        </button>
        {err ? <p className="text-[11px] text-red-400 mt-1 sm:mt-0">{err}</p> : null}
      </form>
    );
  }

  return (
    <div
      className="border border-amber-500/40 bg-amber-500/5 p-5 sm:p-6"
      data-testid={`waitlist-block-${agentSlug}`}
    >
      <p className="text-[10px] uppercase tracking-[0.3em] font-black mb-2" style={{ color: accent }}>
        {ft.wl_full_eyebrow} {eta || "Q3 2026"}
      </p>
      <h3 className="text-2xl sm:text-3xl font-black mb-2 leading-tight text-white">
        {agentName} {ft.wl_full_h3_suffix}
      </h3>
      <p className="text-sm text-gray-300 mb-4 leading-relaxed">
        {ft.wl_full_p_a} <strong className="text-amber-300">{ft.wl_full_offer_free}</strong> + <strong className="text-amber-300">{ft.wl_full_offer_off}</strong> {ft.wl_full_p_b}
      </p>
      <form onSubmit={submit} className="space-y-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={ft.wl_inline_placeholder}
          data-testid={`waitlist-email-${agentSlug}`}
          className="w-full bg-black border border-gray-700 px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:border-amber-500 focus:outline-none"
        />
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={ft.wl_full_note_placeholder}
          maxLength={500}
          rows={2}
          data-testid={`waitlist-note-${agentSlug}`}
          className="w-full bg-black border border-gray-700 px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:border-amber-500 focus:outline-none resize-none"
        />
        <button
          type="submit"
          disabled={state === "sending"}
          data-testid={`waitlist-submit-${agentSlug}`}
          className="w-full bg-amber-500 text-black font-black px-4 py-3 text-sm uppercase tracking-widest hover:bg-amber-400 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {state === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          {ft.wl_full_cta}
        </button>
        {err ? <p className="text-xs text-red-400">{err}</p> : null}
      </form>
    </div>
  );
}
